import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  Input,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { APPS_DOMAINS_MAP, AppsDomainsMap } from '@freelancer/config';
import { Datastore } from '@freelancer/datastore';
import type { LanguagesCollection } from '@freelancer/datastore/collections';
import { Localization } from '@freelancer/localization';
import { ButtonColor } from '@freelancer/ui/button';
import { IconColor } from '@freelancer/ui/icon';
import type { SelectItem } from '@freelancer/ui/select';
import { FontColor, FontWeight, TextSize } from '@freelancer/ui/text';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LanguageSwitcher } from './language-switcher.service';
import { LanguageSwitcherTheme } from './language-switcher.types';

@Component({
  selector: 'fl-language-switcher-inline',
  template: `
    <div
      *ngIf="!compact"
      [flMarginBottom]="'xxxsmall'"
      [flMarginBottomTablet]="'xxsmall'"
    >
      <fl-label
        i18n="Language switcher label"
        for="language-select"
        [weight]="FontWeight.BOLD"
        [size]="TextSize.INHERIT"
        [sizeTablet]="TextSize.SMALL"
        [color]="FontColor.LIGHT"
      >
        Language
      </fl-label>
    </div>
    <div class="ContentWrapper">
      <fl-select
        [control]="languageControl"
        [options]="languages$ | flAsync"
        [flMarginRight]="'xxsmall'"
      >
      </fl-select>
      <fl-button
        i18n="Language switcher button (verb)"
        *ngIf="languageControl.dirty"
        [color]="ButtonColor.SECONDARY"
        (click)="handleUpdate()"
      >
        Update
      </fl-button>
    </div>
  `,
  styleUrls: ['./language-switcher-inline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LanguageSwitcherInlineComponent implements OnInit {
  ButtonColor = ButtonColor;
  FontColor = FontColor;
  FontWeight = FontWeight;
  IconColor = IconColor;
  LanguageSwitcherTheme = LanguageSwitcherTheme;
  TextSize = TextSize;

  languageControl = new FormControl(this.localization.languageCode, {
    nonNullable: true,
  });

  languages$: Observable<readonly SelectItem[]>;

  @Input() compact = false;

  constructor(
    private localization: Localization,
    private datastore: Datastore,
    private languageSwitcher: LanguageSwitcher,
    @Inject(APPS_DOMAINS_MAP) private appsDomainsMap: AppsDomainsMap,
    @Inject(UI_CONFIG) private uiConfig: UiConfig,
  ) {}

  ngOnInit(): void {
    const languageCodes = Object.keys(
      this.appsDomainsMap[this.uiConfig.theme ?? 'main'],
    ).map(
      // Remove -CUSTOM suffix to support flexible terminology
      code => code.replace('-CUSTOM', ''),
    );

    this.languages$ = this.datastore
      .collection<LanguagesCollection>('languages', query =>
        query.where('code', 'in', languageCodes),
      )
      .valueChanges()
      .pipe(
        map(languages =>
          languages.map(language => ({
            value: language.code,
            displayText: language.name,
          })),
        ),
      );
  }

  handleUpdate(): void {
    this.languageSwitcher.switchLanguage(this.languageControl.value);
  }
}
